import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Divider,
  SvgIcon,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { withStyles } from 'tss-react/mui';
import AddLinkIcon from '@mui/icons-material/AddLink';
import AutocompleteFieldFormik from 'src/components/AutocompleteFieldFormik';
import FormEntityChooser from 'src/components/SelectDynamicJoinFormUrl/FormEntityChooser';
import PersonalizedLinkCheckbox from 'src/components/SelectDynamicJoinFormUrl/PersonalizedLinkCheckbox';
import Tooltip from 'src/components/Tooltip';
import { getFormsList, clearFormsList } from 'src/actions/formPartyJoinActions';
import helpers from 'src/helpers';
import { STORAGE, PERMISSIONS } from 'src/constants';
import axios from 'src/utils/axios';

import selectDynamicFieldStyle from 'src/components/SelectDynamicField/selectDynamicField.style';

// Inline styles — duplicated from selectDynamicFieldStyle so production builds
// are immune to tss-react / MUI CSS injection order issues.
const INLINE = {
  root: {
    position: 'relative',
    display: 'inline-block',
    width: 32,
    height: 32,
    border: '1px solid #e0e0e0',
    borderRadius: 5,
    textAlign: 'center',
    cursor: 'pointer',
    marginLeft: 4,
    backgroundColor: '#FFFFFF',
  },
  divBtn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    color: '#5850EC',
  },
  dropdown: {
    position: 'absolute',
    display: 'block',
    top: 35,
    left: -356,
    width: 390,
    height: 'auto',
    padding: '20px 21px',
    backgroundColor: '#ffffff',
    borderRadius: 4,
    border: '1px solid #C7C7C7',
    zIndex: 250,
    cursor: 'default',
  },
  btnPrimary: {
    color: '#ffffff',
    background: '#5850EC',
    boxShadow: '0px 0px 0px 1px #5850EC',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 400,
    padding: '5px 20px',
    marginLeft: 8,
  },
  btnCancel: {
    color: '#ffffff',
    background: '#ABABB7',
    boxShadow: '0px 0px 0px 1px #ABABB7',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 400,
    padding: '5px 20px',
    marginLeft: 8,
  },
  buttonLine: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: 12,
    borderBottom: '1px solid #E0E0E0',
  },
  formBox: {
    paddingTop: 12,
  },
  groupLabel: {
    color: '#393939',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '16px',
    padding: '0px 0px 10px 0px',
  },
  optionValue: {
    color: '#5850EC',
    fontFamily: 'Roboto',
    fontSize: '12px',
  },
  optionImage: {
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: 80,
  },
  optionImg: {
    width: 80,
    height: 80,
    maxWidth: 80,
    maxHeight: 80,
    objectFit: 'contain',
    border: '1px solid #CCCCCC',
    borderRadius: 5,
    padding: 4,
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
  },
};

/* *************************************************************************
 * buildFormToken
 * Constructs the correct token format based on the selected form's entity
 * kind and the personalization setting.
 *
 * Entity kind is decided by the selected form's own category (V2 Smart
 * Forms), falling back to the editor's entity context for V1/legacy options
 * that carry no category.
 *
 * When a specific entity instance id (formEntityId — a partyId or groupId) is
 * present, it is baked into the token (party/group-level editors). Type-level
 * editors (Event/Group Action Rules) have no single id — the rule spans many
 * parties/groups — so they emit an id-LESS entity token that the send-time
 * resolver fills from the party/group being sent for. Baking any id here would
 * bind the wrong instance.
 *
 * V1/legacy (no formCategory) never emitted id-less entity tokens; that
 * behavior is preserved — without a category AND without an id it falls
 * through to the contact/anonymous shape exactly as before.
 *************************************************************************** */
export function buildFormToken({
  formCode,
  formCategory,
  isEventContext,
  isGroupContext,
  formEntityId,
  personalized,
}) {
  if (!formCode) return null;

  const isGroupForm = formCategory ? formCategory === 'Group' : isGroupContext;
  const isEventForm = formCategory ? formCategory === 'Event' : isEventContext;
  const allowIdLess = Boolean(formCategory);

  if (isGroupForm && (formEntityId || allowIdLess)) {
    if (formEntityId) {
      return personalized
        ? `[fpg:${formCode}:${formEntityId}]`
        : `[fg:${formCode}:${formEntityId}]`;
    }
    return personalized ? `[fpg:${formCode}]` : `[fg:${formCode}]`;
  }
  if (isEventForm && (formEntityId || allowIdLess)) {
    if (formEntityId) {
      return personalized
        ? `[fpe:${formCode}:${formEntityId}]`
        : `[fe:${formCode}:${formEntityId}]`;
    }
    return personalized ? `[fpe:${formCode}]` : `[fe:${formCode}]`;
  }

  // Contact form / no entity context
  return personalized
    ? `[fp:${formCode}]`
    : `[f:${formCode}]`;
}

/* *************************************************************************
 * hasPersonalizedFormToken
 * CON-1854 — true when text carries a personalized form link token, i.e. one
 * of the [fp:...], [fpe:...] or [fpg:...] shapes buildFormToken emits with
 * personalized set (Connect Pages reuse [fp:]). Anonymous [f:], [fe:], [fg:]
 * tokens and [Join Form URL] never identify the recipient, so they do not
 * count. Mirrors the server's personalized-token check in
 * followUpMessagesService.
 *************************************************************************** */
const PERSONALIZED_FORM_TOKEN_REGEX = /\[fp[eg]?:[^\]\s]+\]/;

export function hasPersonalizedFormToken(text) {
  return typeof text === 'string' && PERSONALIZED_FORM_TOKEN_REGEX.test(text);
}

function SelectDynamicJoinFormUrl({
  classes,
  addField,
  currentValue,
  externalOpen = null,
  onExternalOpenHandled = () => {},
  entityType = null,
  formEntityId = null,
  allowTypeLevelEntityForms = false,
  requireSpecificEntity = false,
  perRecipientScope = 'send',
  lockPersonalized = false,
  // Host template editors set this: host-sent templates go out from the
  // host's own device to people who are not CP contacts, so a
  // personalized link can never resolve. Hides the personalization
  // checkbox entirely and always emits anonymous tokens. Wins over
  // lockPersonalized (the two never co-occur today).
  hideFormPersonalization = false,
  // CON-1854 — where the box starts. Rule editors whose recipients are Hosts
  // only pass false (hosts forward links to guests); everywhere else keeps
  // the checked default. Re-applied when it changes (e.g. the rule's
  // conditions change), so it is a starting point, never a lock.
  defaultPersonalized = true,
  // CON-1587 — 'cpPage' turns this into a CP Page picker: it lists ONLY CP
  // Pages (never a join target, so no entity chooser), keeps the personalization
  // toggle (a personalized link identifies who visited), and reuses the Contact
  // token family. 'smartForm' (default) lists forms and EXCLUDES CP Pages.
  mode = 'smartForm',
}) {
  const isCpPageMode = mode === 'cpPage';
  const [openSelect, setOpenSelect] = useState(false);
  const [personalized, setPersonalized] = useState(defaultPersonalized);
  useEffect(() => {
    setPersonalized(defaultPersonalized);
  }, [defaultPersonalized]);
  // CON-1585: some contexts (keyword replies) must ALWAYS personalize the
  // link — an id-baked personalized token is the only shape that resolves
  // there. lockPersonalized force-checks and disables the toggle; the flag
  // defaults off so every other call site keeps its live toggle.
  // hideFormPersonalization forces anonymous tokens regardless of any
  // default-checked state (host template editors).
  let effectivePersonalized = lockPersonalized ? true : personalized;
  if (hideFormPersonalization) effectivePersonalized = false;
  // Entity binding for a selected Event/Group form: per-recipient (id-less
  // token) or a specific chosen entity (id-ful token). Defaulted when the
  // form selection changes; null while a Contact/V1 form is selected.
  const [entityChoice, setEntityChoice] = useState(null);
  const dispatch = useDispatch();
  const elRef = useRef(null);
  const { user, azAccountName, databaseBeingUsed } = useSelector((state) => state?.account || {});
  const { formsList } = useSelector((state) => state?.formPartyJoin || {});

  const {
    id: userId,
  } = user;

  const userRolePermissions = user?.userRole?.permissions || [];
  const hasV2Permission = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_FORMS_VIEW,
    userRolePermissions,
  );

  // V2: local state for Smart Forms fetched from /api/smart-forms
  const [v2Forms, setV2Forms] = useState([]);
  const [v2Loading, setV2Loading] = useState(false);

  // V1: forms from Redux (existing behavior)
  const {
    forms: v1Forms,
    loading: v1Loading,
  } = formsList || {};

  const initialValues = {
    selectedForm: null,
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
  });

  const {
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    setFieldTouched,
    setFieldValue,
    resetForm,
  } = formik;

  // entityType may arrive as a string ('PartyGuest') or an array
  // (['PartyGuest'] — e.g. the Action Rules picker passes an array). Normalize
  // to membership checks so both shapes resolve the same context.
  const entityTypeList = Array.isArray(entityType) ? entityType : [entityType];
  const isGroupContext = entityTypeList.includes('GroupMember');
  const isEventContext = entityTypeList.includes('PartyGuest');

  // Ambient/per-recipient are only valid for the form kind MATCHING the
  // screen's context: a partyId ambient must never seed a Group form's id
  // (and vice versa), and per-recipient needs send context of the same kind.
  // Cross-type picks (e.g. Group form from an event screen) therefore fall
  // through to specific-entity-required.
  const kindMatchesContext = (cat) => (
    (cat === 'Event' && isEventContext) || (cat === 'Group' && isGroupContext)
  );
  const ambientForCategory = (cat) => (kindMatchesContext(cat) ? formEntityId : null);

  const shouldFieldBeClose = (e) => (
    !helpers.hasParentClass(e.target, '.MuiAutocomplete-popper')
    && !helpers.hasParentClass(e.target, '.MuiChip-deleteIcon')
    // The entity chooser's mode Select renders its menu in a portal
    // (.MuiPopover-root) outside this component's DOM — clicks there must
    // not close the picker.
    && !helpers.hasParentClass(e.target, '.MuiPopover-root')
  );

  const handleClickAnywhere = (e) => {
    if (elRef?.current && !elRef.current.contains(e.target) && shouldFieldBeClose(e)) {
      setOpenSelect(false);
      document.removeEventListener('click', handleClickAnywhere);
    }
  };

  const handleOnClickApply = () => {
    const selected = values.selectedForm;
    if (!selected?.formCode) {
      setOpenSelect(false);
      resetForm();
      return;
    }

    // Entity binding for Event/Group Smart Forms: per-recipient -> id-less
    // token (send context supplies each recipient's entity); specific ->
    // id-ful token with the chosen entity. Contact/V1 forms keep the legacy
    // ambient formEntityId behavior untouched.
    const isEntityForm = selected.formCategory === 'Event' || selected.formCategory === 'Group';
    let entityIdForToken = formEntityId;
    if (hasV2Permission && isEntityForm && entityChoice) {
      entityIdForToken = entityChoice.mode === 'perRecipient'
        ? null
        : (entityChoice.entityId ?? ambientForCategory(selected.formCategory) ?? null);
      // Specific mode with no entity chosen never emits (Insert is disabled
      // for this case; belt-and-suspenders guard).
      if (entityChoice.mode === 'specific' && !entityIdForToken) {
        return;
      }
    }

    const token = buildFormToken({
      formCode: selected.formCode,
      formCategory: selected.formCategory,
      isEventContext,
      isGroupContext,
      formEntityId: entityIdForToken,
      personalized: hasV2Permission && effectivePersonalized,
    });
    if (token) {
      addField(token, currentValue);
    }
    setOpenSelect(false);
    resetForm();
  };

  const handleOnClickCancel = () => {
    resetForm();
  };

  const handleClick = () => {
    setOpenSelect(!openSelect);
    if (!openSelect) {
      document.addEventListener('click', handleClickAnywhere);
      return;
    }
    document.removeEventListener('click', handleClickAnywhere);
  };

  // Allow parent to programmatically open this picker
  useEffect(() => {
    if (externalOpen && !openSelect) {
      setTimeout(() => {
        handleClick();
      }, 0);
      onExternalOpenHandled();
    }
  }, [externalOpen]);

  const handleClickApply = () => {
    handleOnClickApply();
  };

  const handleClickCancel = () => {
    handleOnClickCancel();
    setOpenSelect(false);
  };

  useEffect(() => () => document.removeEventListener('click', handleClickAnywhere), []);

  // Default the entity binding whenever the selected form changes:
  // matching-kind ambient -> specific, pre-selected;
  // matching-kind, no instance -> per-recipient;
  // cross-type or entity-free -> specific, must choose.
  useEffect(() => {
    const cat = values.selectedForm?.formCategory;
    if (cat !== 'Event' && cat !== 'Group') {
      setEntityChoice(null);
      return;
    }
    const ambient = ambientForCategory(cat);
    if (ambient) {
      setEntityChoice({ mode: 'specific', entityId: ambient });
    } else if (!requireSpecificEntity && kindMatchesContext(cat)) {
      setEntityChoice({ mode: 'perRecipient', entityId: null });
    } else {
      setEntityChoice({ mode: 'specific', entityId: null });
    }
  }, [values.selectedForm, formEntityId, requireSpecificEntity]);

  // V1: fetch from Redux (existing endpoint)
  useEffect(() => {
    if (!hasV2Permission) {
      dispatch(getFormsList());
      return () => {
        dispatch(clearFormsList());
      };
    }
    return undefined;
  }, [hasV2Permission]);

  // V2: fetch Smart Forms from /api/smart-forms
  useEffect(() => {
    if (!hasV2Permission) return undefined;

    let cancelled = false;
    const fetchV2Forms = async () => {
      setV2Loading(true);
      try {
        // CON-1587 — CP Page mode fetches only CP Pages; every other mode
        // excludes them so they never appear as an insertable Smart Form.
        const params = { status: 'Active', pageSize: 5000 };
        if (isCpPageMode) params.formType = 'CpPage';
        else params.excludeCpPages = true;
        const resp = await axios.get('/api/smart-forms', { params });
        if (!cancelled) {
          setV2Forms(resp?.data?.forms || []);
        }
      } catch (e) {
        if (!cancelled) setV2Forms([]);
      }
      if (!cancelled) setV2Loading(false);
    };
    fetchV2Forms();
    return () => { cancelled = true; };
  }, [hasV2Permission, isCpPageMode]);

  // An entity-instance id (formEntityId) OR an explicit type-level opt-in
  // (allowTypeLevelEntityForms — e.g. Smart Action editors, which have an
  // Event/Group context but no specific party/group instance) both count as
  // being in an Event/Group context for category filtering.
  const entityActive = formEntityId || allowTypeLevelEntityForms;

  // Build options based on permission
  let formOptions = [];
  let isLoading = false;

  if (hasV2Permission) {
    isLoading = v2Loading;

    // Filter: Active only. Every upgraded screen (entity-context OR
    // entity-free-with-chooser) offers ALL form types — cross-type picks
    // (e.g. a Group form from an event screen) are safe because the entity
    // chooser requires a matching SPECIFIC entity before Insert. Screens
    // passing none of the entity props keep the legacy Contact-only offer.
    const filtered = v2Forms.filter((f) => {
      if ((f.status || 'Active') !== 'Active') return false;
      // CON-1587 — CP Page mode lists only CP Pages; every other mode drops them
      // (defensive: the fetch already filters server-side).
      if (isCpPageMode) return f.isCpPage === true;
      if (f.isCpPage) return false;
      if (requireSpecificEntity || entityActive) {
        return ['Event', 'Group', 'Contact'].includes(f.formCategory);
      }
      return f.formCategory === 'Contact';
    });

    formOptions = filtered.map((f) => ({
      value: f.id,
      label: f.formName || f.formId,
      formCode: f.formId,
      formCategory: f.formCategory,
    }));
  } else {
    // V1: existing behavior — Event forms only
    isLoading = v1Loading;
    const eventForms = v1Forms && v1Forms.map((eventForm) => ({
      value: eventForm?.partyId,
      label: eventForm?.partyName,
      formCode: eventForm?.joinFormId,
      startDate: eventForm?.partyStartDateTime,
      endDate: eventForm?.partyEndDateTime,
      imageSrc: helpers.azure.getImageFileURL(
        eventForm?.formImage,
        userId,
        azAccountName,
        STORAGE.sas,
        databaseBeingUsed,
        eventForm?.formImageContainer,
        true,
      ),
    }));
    formOptions = eventForms || [];
  }

  const handleFocusFieldValidate = (event) => {
    helpers.formValidation.validateField(event, setFieldTouched);
  };

  // V1 render option with image + dates
  const renderOptionV1 = (props, option) => (
    <Box
      {...props}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box>
          <b>{option.label}</b>
          <Box style={INLINE.optionValue}>
            Dates:
            {' '}
            {`${moment(option.startDate).format('MM/DD/YY')} - ${moment(option.endDate).format('MM/DD/YY')}`}
          </Box>
        </Box>
        <Box style={INLINE.optionImage}>
          <img
            src={option?.imageSrc}
            alt={option?.label || ''}
            style={INLINE.optionImg}
          />
        </Box>
      </Box>
      <Box width="100%">
        <Divider
          sx={{
            marginTop: '10px',
          }}
        />
      </Box>
    </Box>
  );

  // V2 render option — simple label with category badge
  const renderOptionV2 = (renderProps, option) => (
    <Box
      {...renderProps}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
      }}
    >
      <Box>
        <b>{option.label}</b>
        {/* CON-1587 (Bug A) — a Connect Page is stored formCategory=Contact;
            in Connect Page mode show its real type, not "Contact". */}
        {(isCpPageMode || option.formCategory) && (
          <Box style={INLINE.optionValue}>
            {isCpPageMode ? 'Connect Page' : option.formCategory}
          </Box>
        )}
      </Box>
    </Box>
  );

  const tooltipText = isCpPageMode ? 'Insert Connect Page URL' : 'Insert Smart Form URL';
  const selectLabel = isCpPageMode
    ? 'Select a Connect Page'
    : (hasV2Permission ? 'Select a Smart Form' : 'Select Form from Events');

  // CON-1587 — the personalization help differs by context. A CP Page has no
  // fields to skip; its personalized link is about attribution (who opened it).
  let personalizationHelp;
  if (lockPersonalized) {
    personalizationHelp = (
      <>
        Keyword replies always personalize the link to the
        contact who texted in, so CP recognizes them.
      </>
    );
  } else if (isCpPageMode) {
    personalizationHelp = (
      <>
        When checked, each contact gets a unique link that tells you who
        opened the page. Best for messages sent directly to individual
        contacts.
        <br />
        <br />
        If you&apos;re sending a link that will be shared or forwarded
        (e.g., a host sharing with others), uncheck this.
      </>
    );
  } else {
    personalizationHelp = (
      <>
        When checked, each contact receives a unique link
        that recognizes them by name and lets them skip
        fields you already have on file. Best for messages
        sent directly to individual contacts.
        <br />
        <br />
        If you&apos;re sending a link that will be shared
        or forwarded (e.g., a host sharing with party
        guests), uncheck this.
      </>
    );
  }

  // The chooser applies to selected Event/Group Smart Forms only.
  const selectedFormCategory = values.selectedForm?.formCategory;
  const isEntityFormSelected = hasV2Permission
    && (selectedFormCategory === 'Event' || selectedFormCategory === 'Group');
  // Specific mode with no entity chosen blocks Insert (mandatory on
  // entity-free screens; also guards a cleared Autocomplete elsewhere).
  const insertBlockedByEntityChoice = isEntityFormSelected
    && entityChoice?.mode === 'specific'
    && !entityChoice?.entityId;

  return (
    <div
      className={`${classes.root} withBtn inner`}
      ref={elRef}
      style={externalOpen !== null ? {
        ...INLINE.root,
        width: 0,
        height: 0,
        border: 'none',
        overflow: 'visible',
        backgroundColor: 'transparent',
      } : INLINE.root}
    >
      {externalOpen === null && (
        <Tooltip
          title={tooltipText}
          placement="left-end"
        >
          <div
            className="divBtn"
            style={INLINE.divBtn}
            onClick={handleClick}
          >
            <SvgIcon
              component={AddLinkIcon}
            />
          </div>
        </Tooltip>
      )}
      {openSelect && (
        <div className={classes.innerFieldDropDown} style={INLINE.dropdown}>
          <div style={INLINE.buttonLine}>
            <Button
              style={INLINE.btnCancel}
              onClick={handleClickCancel}
            >
              Cancel
            </Button>
            <Button
              style={{
                ...INLINE.btnPrimary,
                ...((!values.selectedForm || insertBlockedByEntityChoice)
                  ? { opacity: 0.5, pointerEvents: 'none' } : {}),
              }}
              onClick={handleClickApply}
              disabled={!values.selectedForm || insertBlockedByEntityChoice}
            >
              Insert
            </Button>
          </div>
          <div style={INLINE.formBox}>
            <Grid>
              <div style={INLINE.groupLabel}>
                {selectLabel}
              </div>
            </Grid>
            <Grid
              spacing={2}
              container
            >
              <Grid
                xs={12}
              >
                {hasV2Permission && !isLoading && formOptions.length === 0 ? (
                  <Box sx={{
                    color: '#888', fontSize: '13px', textAlign: 'center', py: 2,
                  }}
                  >
                    {`No ${(requireSpecificEntity || entityActive) ? '' : 'contact '}forms available.`}
                  </Box>
                ) : (
                  <AutocompleteFieldFormik
                    touchedValue={touched.selectedForm}
                    errorValue={errors.selectedForm}
                    name="selectedForm"
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleFocus={handleFocusFieldValidate}
                    values={values.selectedForm}
                    disabled={false}
                    loading={isLoading}
                    label={selectLabel}
                    setFieldValue={setFieldValue}
                    options={formOptions}
                    renderOption={hasV2Permission ? renderOptionV2 : renderOptionV1}
                    isOptionEqualToValue={helpers.isOptionEqualToValue}
                    multiple={false}
                    disableCloseOnSelect={false}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                  />
                )}
              </Grid>
            </Grid>
            {isEntityFormSelected && entityChoice && (
              <FormEntityChooser
                entityKind={selectedFormCategory}
                // Per-recipient only makes sense where the send spans MANY
                // entities (Event/Group LIST multi-select, type-level SA
                // editors). Single-entity ambient screens (guest tab, group
                // contact tab, per-instance SA editors) hide the mode
                // dropdown entirely — the entity is pre-selected for the
                // user (still changeable to another entity).
                perRecipientAvailable={!requireSpecificEntity
                  && kindMatchesContext(selectedFormCategory)
                  && !ambientForCategory(selectedFormCategory)}
                ambientEntityId={ambientForCategory(selectedFormCategory)}
                // This custom dropdown has a document-level outside-click
                // closer — portaled menus would read as outside clicks.
                disablePortal
                perRecipientScope={perRecipientScope}
                value={entityChoice}
                onChange={setEntityChoice}
              />
            )}
            {hasV2Permission && !hideFormPersonalization && (
              // CON-1852 — warning styling while checked. Not shown when
              // lockPersonalized: keyword replies go back to the one contact
              // who texted in, and the box cannot be unchecked there.
              <PersonalizedLinkCheckbox
                checked={effectivePersonalized}
                onChange={(e) => setPersonalized(e.target.checked)}
                disabled={lockPersonalized || !values.selectedForm}
                helpText={personalizationHelp}
                showWarning={effectivePersonalized && !lockPersonalized}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SelectDynamicJoinFormUrl.propTypes = {
  classes: PropTypes.object.isRequired,
  addField: PropTypes.func.isRequired,
  currentValue: PropTypes.string.isRequired,
  externalOpen: PropTypes.bool,
  onExternalOpenHandled: PropTypes.func,
  entityType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  formEntityId: PropTypes.number,
  allowTypeLevelEntityForms: PropTypes.bool,
  requireSpecificEntity: PropTypes.bool,
  perRecipientScope: PropTypes.oneOf(['selection', 'send', 'submission']),
  lockPersonalized: PropTypes.bool,
  hideFormPersonalization: PropTypes.bool,
  defaultPersonalized: PropTypes.bool,
  mode: PropTypes.oneOf(['smartForm', 'cpPage']),
};

export default withStyles(SelectDynamicJoinFormUrl, selectDynamicFieldStyle);
